import { Pool_SummaryManager } from "./Pool_SummaryManager.js";
import { Pool_DataManager } from "./Pool_DataManager.js";
import { Pool_CartManager } from "./Pool_CartManager.js";
import { Pool_Style } from "./Pool_Style.js";
import { Pool_Shape } from "./Pool_Shape.js";
import { Pool_Size } from "./Pool_Size.js";
import { Pool_Liner } from "./Pool_Liner.js";
import { Pool_Equipment } from "./Pool_Equipment.js";

class PoolConfigurator {
    constructor() {
        this.steps = {};
        this.smartWizard = null;
        this.summaryManager = new Pool_SummaryManager();
        this.dataManager = new Pool_DataManager();
        this.cartManager = new Pool_CartManager(this);

        this.state = {
            selectedPool: null,
            selectedLiner: null,
            selectedEquipment: null,
            selectedSkimmer: null,
        };

        this.init();
    }

    /**
     * Initializes the configurator by loading data and setting up the wizard.
     */
    async init() {
        await this.loadInitialData();
        this.initSmartWizard();
    }

    /**
     * Fetches product data and initializes step objects.
     */
    async loadInitialData() {
        try {
            const { pools, liners, skimmer, filters, pumps } = await this.dataManager.fetchProductData();

            this.state.selectedSkimmer = Array.isArray(skimmer) ? skimmer[0] : skimmer;

            this.steps = {
                "pool-style": new Pool_Style(pools, this),
                "pool-shape": new Pool_Shape(pools, this),
                "pool-size": new Pool_Size(pools, this),
                "liner": new Pool_Liner(liners, this),
                "equipment": new Pool_Equipment(filters, pumps, this),
                "review": this.cartManager,
            };

            this.loadStep(0);
        } catch (error) {
            console.error("Error loading data:", error);
        }
    }

    /**
     * Initializes the SmartWizard plugin with configuration.
     */
    initSmartWizard() {
        this.smartWizard = $("#smartwizard").smartWizard({
            theme: "default",
            autoAdjustHeight: false,
            enableUrlHash: false,
            toolbar: { showNextButton: false },
            keyboard: { keyNavigation: false },
            anchor: {
                enableNavigation: true,
                enableDoneState: true,
                markPreviousStepsAsDone: true,
                unDoneOnBackNavigation: true,
                enableDoneStateNavigation: true,
            },
            noForwardJumping: true,
            enableAllSteps: false,
        });

        this.smartWizard.on("leaveStep", (e, anchorObject, currentStepIndex, nextStepIndex, stepDirection) => {
            if (stepDirection === "backward") {
                this.handleBackwardNavigation(nextStepIndex);
            }
        });
    }

    /**
     * Handles logic when navigating backward through the wizard.
     * @param {number} nextStepIndex - The index of the step the user is navigating to.
     */
    handleBackwardNavigation(nextStepIndex) {
        $('html, body').animate({
            scrollTop: $('html').offset().top
        }, 500);
        
        if (nextStepIndex < 3) {
            this.clearSelections(["selectedLiner", "selectedEquipment"]);
            this.summaryManager.clearSummary();
        }
        if (nextStepIndex === 3) { // ✅ This is Step 4 (Liner Selection)
            // ✅ Reset liner selection to the default cheapest in-stock liner
            this.clearSelections(["selectedLiner", "selectedEquipment"]);
    
            // ✅ Re-render Step 4 to automatically select the cheapest liner again
            const poolLinerStep = this.steps["liner"];
            if (poolLinerStep) {
                poolLinerStep.render(); // ✅ This resets the liner selection
            }
    
            // ✅ Update the summary to show only the default liner
            this.summaryManager.updateSummary(
                this.state.selectedPool,
                this.state.selectedLiner, // This will now be the default liner
                this.state.selectedSkimmer,
                this.state.selectedEquipment
            );
        }
        if (nextStepIndex === 4) { // Step 5 (Equipment)
            this.clearSelections(["selectedEquipment"]);
            this.summaryManager.clearEquipmentSummary(
                this.state.selectedPool,
                this.state.selectedSkimmer,
                this.state.selectedLiner
            );
        }
    
        this.updateLayout(nextStepIndex);
        this.unsetDoneState(nextStepIndex);
    }
    
    /**
     * Clears selected state values based on given keys.
     * @param {string[]} keys - Array of state keys to reset.
     */
    clearSelections(keys) {
        keys.forEach(key => this.state[key] = null);
    }

    /**
     * Unsets the "done" state for all steps ahead of the given index.
     * @param {number} nextStepIndex - Index of the step to retain the "done" state.
     */
    unsetDoneState(nextStepIndex) {
        const totalSteps = this.smartWizard.find("ul.nav").children().length;
        this.smartWizard.smartWizard("unsetState", [...Array(totalSteps - nextStepIndex - 1)].map((_, i) => i + nextStepIndex + 1), "done");
    }

    /**
     * 
     * Loads a specific step in the wizard.
     * @param {number} stepIndex - The index of the step to render.
     */
    loadStep(stepIndex) {
        const stepKeys = Object.keys(this.steps);
        if (this.steps[stepKeys[stepIndex]]) {
            this.steps[stepKeys[stepIndex]].render();
        }

        this.updateLayout(stepIndex);
    }

    /**
     * Updates the layout based on the current step.
     * @param {number} stepIndex - The current step index.
     */
    updateLayout(stepIndex) {
        const summaryContainer = document.getElementById("summary");
        const smartWizardContainer = document.getElementById("smartwizard");

        if (stepIndex === 5) { // Step 6 (Review)
            summaryContainer.style.display = "none";
            smartWizardContainer.classList.remove("col-lg-10");
            smartWizardContainer.classList.add("col-lg-12");
        } else {
            summaryContainer.style.display = "block";
            smartWizardContainer.classList.remove("col-lg-12");
            smartWizardContainer.classList.add("col-lg-10");
        }
    }

    /**
     * Handles selection of an option and advances to the next step.
     * @param {string} stepKey - The key of the current step.
     */
    handleSelection(stepKey) {
        if (!this.steps[stepKey]) return;

        this.loadStep(Object.keys(this.steps).indexOf(stepKey) + 1);
        $("#smartwizard").smartWizard("next");

        if (stepKey === "pool-shape") this.clearSelections(["selectedLiner", "selectedEquipment"]);
        if (stepKey === "liner") this.clearSelections(["selectedEquipment"]);

        this.updateSummaryAndCart(stepKey);
    }

    /**
     * Updates the summary and cart based on the current step.
     * @param {string} stepKey - The key of the step being processed.
     */
    updateSummaryAndCart(stepKey) {
        if (["pool-size", "liner", "equipment"].includes(stepKey)) {
            this.summaryManager.updateSummary(
                this.state.selectedPool,
                this.state.selectedLiner,
                this.state.selectedSkimmer,
                this.state.selectedEquipment
            );
        }

        if (stepKey === "equipment") {
            this.cartManager.updateCart(
                this.state.selectedPool,
                this.state.selectedLiner,
                this.state.selectedSkimmer,
                this.state.selectedEquipment
            );
        }
    }
}

document.addEventListener("DOMContentLoaded", () => {
    window.poolConfigurator = new PoolConfigurator();
});
